import { RenderPass, VAOInputType } from "../../../renderSystem/RenderPass";
import {
  ResourceCache,
  getUniformLocations
} from "../../../renderSystem/managers/ResourceCache";
import { RenderGraph } from "../../../renderSystem/RenderGraph";
import { RenderUtils } from "../../../../utils/RenderUtils";
import { RenderTarget } from "../../../renderSystem/RenderTarget";
import { VaoInfo } from "../../../renderSystem/managers/VaoManager";
import { PointLight } from "../../../../map/Light";
import { mat4, vec3 } from "gl-matrix";
import CubeShadowsVertexShaderSource from "../../../glsl/DeferredRendering/Shadows/PointShadow/CubeShadows.vert";
import CubeShadowsFragmentShaderSource from "../../../glsl/DeferredRendering/Shadows/PointShadow/CubeShadows.frag";
import { SettingsManager } from "../../../../Settings";

export class CubeShadowsPass extends RenderPass {
  public pathtracerRender: boolean = false;
  public VAOInputType: VAOInputType = VAOInputType.SCENE;
  private currentLightIndex: number = 0;
  constructor(
    gl: WebGL2RenderingContext,
    resourceCache: ResourceCache,
    canvas: HTMLCanvasElement,
    renderGraph?: RenderGraph
  ) {
    super(gl, resourceCache, canvas, renderGraph);
    this.program = RenderUtils.CreateProgram(
      gl,
      CubeShadowsVertexShaderSource,
      CubeShadowsFragmentShaderSource
    )!;
    this.uniforms = getUniformLocations(gl, this.program!, [
      "lightSpaceMatrix",
      "model",
      "lightPos",
      "lightRadius",
      "lightRange"
    ]);
    this.renderTarget = this.initRenderTarget();
    this.InitSettings();
  }

  public override getInvocationCount(): number {
    return this.resourceCache.getData("numShadowedLights") ?? 0;
  }
  public override setInvocationIndex(index: number): void {
    this.currentLightIndex = index;
  }

  public initRenderTarget(): RenderTarget {
    let size = this.resourceCache.getData("CubeShadowsMapSize") ?? 1024;
    // Store the default value if it wasn't set
    if (!this.resourceCache.getData("CubeShadowsMapSize")) {
      this.resourceCache.setData("CubeShadowsMapSize", size);
    }
    const maxTextureSize = this.gl.getParameter(this.gl.MAX_TEXTURE_SIZE);

    // Clamp shadow map size to maximum supported texture size
    if (size > maxTextureSize) {
      console.warn(
        `[Cube Shadows] Shadow map size ${size} exceeds maximum texture size ${maxTextureSize}. Clamping to ${maxTextureSize}.`
      );
      size = maxTextureSize;
      this.resourceCache.setData("CubeShadowsMapSize", size);
    }

    const cubeMaps: WebGLTexture[] = [];

    const fbo = this.gl.createFramebuffer();
    if (!fbo) {
      throw new Error("Failed to create framebuffer");
    }
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, fbo);

    // Create depth cube map textures for each light with hardware shadow comparison
    for (
      let lightIndex = 0;
      lightIndex < this.resourceCache.getData("numShadowedLights");
      lightIndex++
    ) {
      const cubeMap = this.gl.createTexture();
      if (!cubeMap) {
        throw new Error("Failed to create cube map texture");
      }
      this.gl.bindTexture(this.gl.TEXTURE_CUBE_MAP, cubeMap);
      for (let i = 0; i < 6; i++) {
        this.gl.texImage2D(
          this.gl.TEXTURE_CUBE_MAP_POSITIVE_X + i,
          0,
          this.gl.DEPTH_COMPONENT32F,
          size,
          size,
          0,
          this.gl.DEPTH_COMPONENT,
          this.gl.FLOAT,
          null
        );
      }
      // Use LINEAR filtering for hardware shadow filtering
      this.gl.texParameteri(
        this.gl.TEXTURE_CUBE_MAP,
        this.gl.TEXTURE_MIN_FILTER,
        this.gl.LINEAR
      );
      this.gl.texParameteri(
        this.gl.TEXTURE_CUBE_MAP,
        this.gl.TEXTURE_MAG_FILTER,
        this.gl.LINEAR
      );
      this.gl.texParameteri(
        this.gl.TEXTURE_CUBE_MAP,
        this.gl.TEXTURE_WRAP_S,
        this.gl.CLAMP_TO_EDGE
      );
      this.gl.texParameteri(
        this.gl.TEXTURE_CUBE_MAP,
        this.gl.TEXTURE_WRAP_T,
        this.gl.CLAMP_TO_EDGE
      );
      this.gl.texParameteri(
        this.gl.TEXTURE_CUBE_MAP,
        this.gl.TEXTURE_WRAP_R,
        this.gl.CLAMP_TO_EDGE
      );
      // Enable hardware shadow comparison
      this.gl.texParameteri(
        this.gl.TEXTURE_CUBE_MAP,
        this.gl.TEXTURE_COMPARE_MODE,
        this.gl.COMPARE_REF_TO_TEXTURE
      );
      this.gl.texParameteri(
        this.gl.TEXTURE_CUBE_MAP,
        this.gl.TEXTURE_COMPARE_FUNC,
        this.gl.LEQUAL
      );
      cubeMaps.push(cubeMap);
    }

    this.gl.bindTexture(this.gl.TEXTURE_CUBE_MAP, null);
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);

    return {
      fbo: fbo,
      textures: {
        pointShadowTextures: cubeMaps
      }
    };
  }

  public render(vaosToRender: VaoInfo[]): void {
    if (!this.resourceCache.getData("cubeShadowsOn")) {
      return;
    }
    const cubeMaps = this.renderTarget!.textures!
      .pointShadowTextures as WebGLTexture[];
    const currentCubeMap = cubeMaps[this.currentLightIndex];

    const shadowMapSize = this.resourceCache.getData("CubeShadowsMapSize")!;

    // Set up render state once
    this.gl.enable(this.gl.DEPTH_TEST);
    this.gl.depthFunc(this.gl.LEQUAL);
    this.gl.enable(this.gl.CULL_FACE);
    this.gl.disable(this.gl.SCISSOR_TEST); // Disable scissor test to ensure full rendering
    this.gl.useProgram(this.program);
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.renderTarget!.fbo);
    
    // No color output - depth-only rendering
    this.gl.drawBuffers([this.gl.NONE]);
    this.gl.colorMask(false, false, false, false);

    // Set clear values
    this.gl.clearDepth(1.0);

    for (let i = 0; i < 6; i++) {
      if (
        this.resourceCache.getData("lights")!.length <= this.currentLightIndex
      ) {
        continue;
      }
      // Set viewport for this face
      this.gl.viewport(0, 0, shadowMapSize, shadowMapSize);

      // Attach the cube map face to the framebuffer as depth attachment
      this.gl.framebufferTexture2D(
        this.gl.FRAMEBUFFER,
        this.gl.DEPTH_ATTACHMENT,
        this.gl.TEXTURE_CUBE_MAP_POSITIVE_X + i,
        currentCubeMap,
        0
      );

      // Verify framebuffer is complete before clearing
      const status = this.gl.checkFramebufferStatus(this.gl.FRAMEBUFFER);
      if (status !== this.gl.FRAMEBUFFER_COMPLETE) {
        console.error(`Framebuffer incomplete for face ${i}: ${status}`);
        continue;
      }

      // Clear depth for this face
      this.gl.clear(this.gl.DEPTH_BUFFER_BIT);

      // Set up uniforms for this face
      const lightSpaceMatrix = getLightSpaceMatrix(
        this.resourceCache,
        this.resourceCache.getData("lights")![this.currentLightIndex],
        i
      );
      this.gl.uniformMatrix4fv(
        this.uniforms["lightSpaceMatrix"],
        false,
        lightSpaceMatrix
      );
      this.gl.uniform3fv(
        this.uniforms["lightPos"],
        this.resourceCache.getData("lights")![this.currentLightIndex].position
      );
      this.gl.uniform1f(
        this.uniforms["lightRadius"],
        this.resourceCache.getData("lights")![this.currentLightIndex].radius
      );
      this.gl.uniform1f(
        this.uniforms["lightRange"],
        this.resourceCache.getData("lights")![this.currentLightIndex].range
      );

      // Render all geometry for this face
      for (const vaoInfo of vaosToRender) {
        // Skip rendering this light's own sphere (prevent self-shadowing)
        if (vaoInfo.isLight && vaoInfo.lightIndex === this.currentLightIndex) {
          continue;
        }

        this.gl.bindVertexArray(vaoInfo.vao);
        this.gl.uniformMatrix4fv(
          this.uniforms["model"],
          false,
          vaoInfo.modelMatrix
        );
        this.gl.drawElements(
          this.gl.TRIANGLES,
          vaoInfo.indexCount,
          this.gl.UNSIGNED_INT,
          0
        );
      }

      this.gl.bindVertexArray(null);
    }
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
    this.gl.colorMask(true, true, true, true);
  }

  private InitSettings() {
    SettingsManager.instance.createSection(
      document.getElementById("settings-section")!,
      "Cube Shadows Settings"
    );
    this.resourceCache.setData("cubeShadowsOn", true);

    SettingsManager.instance.addCheckboxToSection("Cube Shadows Settings", {
      id: "cubeShadowsOn",
      label: "Cube Shadows On",
      defaultValue: true,
      onChange: (value: boolean) => {
        this.resourceCache.setData("cubeShadowsOn", value);
      }
    });
    SettingsManager.instance.addSliderToSection("Cube Shadows Settings", {
      id: "CubeShadowsMapSize",
      label: "Point Light Shadow Map Size",
      min: 256,
      max: 4096,
      step: 256,
      defaultValue: 1024,
      numType: "int",
      onChange: (value: number) => {
        const newSize = Math.floor(value);
        this.resourceCache.setData("CubeShadowsMapSize", newSize);
        this.disposeRenderTarget();
        this.renderTarget = this.initRenderTarget();
      }
    });
    SettingsManager.instance.addSliderToSection("Cube Shadows Settings", {
      id: "pointShadowBias",
      label: "Point Light Shadow Bias",
      min: 0.0,
      max: 0.1,
      step: 0.001,
      defaultValue: 0.05,
      numType: "float",
      fineTuner: true,
    });
  }
}

function getLightSpaceMatrix(
  resourceCache: ResourceCache,
  light: PointLight,
  face: number
): mat4 {
  const lightSpaceMatrix = mat4.create();
  const lightViewMatrix = mat4.create();
  let target = vec3.create();
  let up = vec3.create();
  switch (face) {
    case 0:
      target = vec3.fromValues(
        light.position[0] + 1,
        light.position[1],
        light.position[2]
      );
      up = vec3.fromValues(0.0, -1.0, 0.0);
      break;
    case 1:
      target = vec3.fromValues(
        light.position[0] - 1,
        light.position[1],
        light.position[2]
      );
      up = vec3.fromValues(0.0, -1.0, 0.0);
      break;
    case 2:
      target = vec3.fromValues(
        light.position[0],
        light.position[1] + 1,
        light.position[2]
      );
      up = vec3.fromValues(0.0, 0.0, 1.0);
      break;
    case 3:
      target = vec3.fromValues(
        light.position[0],
        light.position[1] - 1,
        light.position[2]
      );
      up = vec3.fromValues(0.0, 0.0, -1.0);
      break;
    case 4:
      target = vec3.fromValues(
        light.position[0],
        light.position[1],
        light.position[2] + 1
      );
      up = vec3.fromValues(0.0, -1.0, 0.0);
      break;
    case 5:
      target = vec3.fromValues(
        light.position[0],
        light.position[1],
        light.position[2] - 1
      );
      up = vec3.fromValues(0.0, -1.0, 0.0);
      break;
  }
  mat4.lookAt(lightViewMatrix, light.position, target, up);
  const lightProjectionMatrix = mat4.create();
  // Use the light's range for shadow map far plane
  const shadowMapFar = light.range;
  mat4.perspective(lightProjectionMatrix, Math.PI / 2, 1.0, 0.1, shadowMapFar);
  mat4.multiply(lightSpaceMatrix, lightProjectionMatrix, lightViewMatrix);
  return lightSpaceMatrix;
}
